import { useRef, useState } from 'react'
import { BracketsCurlyIcon, CheckIcon, CircleNotchIcon, InfoIcon } from '@phosphor-icons/react'
import { Button } from 'app/components/ui/button'
import { Input } from 'app/components/ui/input'
import { Label } from 'app/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from 'app/components/ui/popover'
import { TooltipWrapper } from 'app/components/ui/tooltip-wrapper'
import {
  Select as SelectRoot,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from 'app/components/ui/select'
import { Switch } from 'app/components/ui/switch'
import { useListCopy } from 'app/hooks/useListCopy'
import { cn } from 'app/lib/utils'
import {
  type ContactField,
  engineVariableToken,
  groupFieldsBySource,
  labelForToken,
  PAPERWORK_LINK_FIELD,
} from 'app/lib/workflows/dataContext'
import { TokenEditor, type TokenEditorHandle } from './TokenEditor'
import { readFallbacks, tokensUsed, writeFallbacks } from 'app/lib/workflows/messageFallbacks'
import { fillVariables } from 'app/lib/workflows/preview'

/** An "i" next to a label that reveals background detail on hover or focus.
 *
 *  Use `info` for elaboration a user reads once (why a setting exists, what the engine does behind
 *  it). Keep `hint` for anything that changes the choice being made right now: a consequence, a
 *  caveat, a "this will send real messages". Those must stay on screen — a drawer nobody can act on
 *  without hovering is worse than one with a bit of grey text. */
export function InfoHint({ text }: { text: string }) {
  return (
    // pinOnClick drives the open state from pointer/focus rather than leaving it to hover alone,
    // so this also opens on tap. Hover doesn't exist on a phone, and these drawers are going there.
    <TooltipWrapper message={text} placement="top" pinOnClick tooltipClassName="max-w-72 text-[11px] leading-relaxed">
      {/* Sits inside the label's text flow (inline, aligned to the text baseline box) so it hugs the
          final word and wraps with it, instead of drifting to the far edge of a flex row. */}
      <button
        type="button"
        aria-label={text}
        className="ml-1 inline-flex size-4 shrink-0 translate-y-[2px] items-center justify-center rounded-full text-minimal hover:text-subtle focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      >
        <InfoIcon size={13} weight="bold" />
      </button>
    </TooltipWrapper>
  )
}

/** Field wrapper: label + control + (error or hint). `info` puts background detail behind an "i"
 *  on the label instead of adding another line of grey text. */
export function Field({
  label,
  hint,
  info,
  error,
  htmlFor,
  children,
}: {
  label?: string
  /** Always visible, one line under the control. Only for a consequence that changes the choice
   *  being made right now ("Sends real texts to real patients"). Never background explanation. */
  hint?: string
  /** Behind the "i" on the label: why the setting exists, what the engine does with it. At most two
   *  sentences. This is where explanation goes; the design language keeps it off the surface. */
  info?: string
  error?: string
  /** Id of the control, so the label names it for assistive tech and tests. */
  htmlFor?: string
  children: React.ReactNode
}) {
  return (
    <div>
      {label && (
        <Label htmlFor={htmlFor} className="mb-1.5 block text-xs font-medium text-subtle">
          {label}
          {info && <InfoHint text={info} />}
        </Label>
      )}
      {children}
      {error ? (
        <p className="mt-1.5 text-[11px] text-red-600">{error}</p>
      ) : hint ? (
        <p className="mt-1.5 text-[11px] leading-relaxed text-subtle">{hint}</p>
      ) : null}
    </div>
  )
}

export function TextField({
  invalid,
  className,
  ...props
}: React.ComponentProps<typeof Input> & { invalid?: boolean }) {
  return <Input className={cn(`bg-surface`, invalid && `border-red-400`, className)} {...props} />
}

export type Choice = { value: string; label: string }
/** A labeled set of choices — renders as a headed section in the dropdown (e.g. Contact / Activity). */
export type ChoiceGroup = { label: string; choices: readonly Choice[] }

// Radix selects can't hold an empty-string item value, so we swap '' for a sentinel and
// map it back on change. This lets callers keep using '' for "any / none / unset" choices.
const EMPTY = `__empty__`

/** Single-select backed by the app's Radix select, so its dropdown renders as a styled
 *  portal that matches the rest of the app. Pass `choices` for a flat list, or `groups` to
 *  render headed sections (used to label fields by their source object — contact vs activity). */
export function ChoiceSelect({
  value,
  onChange,
  choices,
  groups,
  invalid,
  placeholder,
  className,
  ariaLabel,
}: {
  value: string
  onChange: (value: string) => void
  choices?: readonly Choice[]
  groups?: readonly ChoiceGroup[]
  invalid?: boolean
  placeholder?: string
  className?: string
  ariaLabel?: string
}) {
  const renderItem = (choice: Choice) => (
    <SelectItem key={choice.value} value={choice.value === `` ? EMPTY : choice.value}>
      {choice.label}
    </SelectItem>
  )
  // '' only maps to the sentinel when a choice actually carries it ("Any form"). With no such
  // choice, '' means "nothing picked yet", and Radix shows the placeholder only for a real ''.
  const hasEmptyChoice = (choices ?? groups?.flatMap((group) => group.choices) ?? []).some((c) => c.value === ``)
  return (
    <SelectRoot
      value={value === `` && hasEmptyChoice ? EMPTY : value}
      onValueChange={(v) => onChange(v === EMPTY ? `` : v)}
    >
      <SelectTrigger
        aria-label={ariaLabel}
        className={cn(
          `rounded-lg border-default bg-surface text-body-sm shadow-elevation-1`,
          invalid && `border-red-400`,
          className,
        )}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="max-h-72 border-default shadow-sm">
        {groups
          ? groups.map((group, index) => (
              <SelectGroup key={group.label} className={cn(index > 0 && `mt-1 border-t border-subtle pt-1`)}>
                <SelectLabel>{group.label}</SelectLabel>
                {group.choices.map(renderItem)}
              </SelectGroup>
            ))
          : (choices ?? []).map(renderItem)}
      </SelectContent>
    </SelectRoot>
  )
}

/** Boolean toggle with a label, an optional hint, and optional background detail behind an "i". */
export function ToggleField({
  label,
  hint,
  info,
  checked,
  onChange,
}: {
  label: string
  hint?: string
  info?: string
  checked: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <Label className="block text-xs font-medium text-subtle">
          {label}
          {info && <InfoHint text={info} />}
        </Label>
        {hint && <p className="mt-0.5 text-[11px] leading-relaxed text-subtle">{hint}</p>}
      </div>
      <Switch checked={checked} onCheckedChange={onChange} className="mt-0.5 shrink-0" />
    </div>
  )
}

/** Multi-select with a chip summary and a checklist popover. */
export function MultiSelect({
  choices,
  selected,
  onChange,
  placeholder,
  invalid,
  loading,
}: {
  choices: readonly Choice[]
  selected: string[]
  onChange: (selected: string[]) => void
  placeholder: string
  invalid?: boolean
  /** Show a loading state (spinner + "Loading…") and disable opening while options are still fetching. */
  loading?: boolean
}) {
  const [open, setOpen] = useState(false)
  const toggle = (value: string) =>
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value])
  const labelFor = (value: string) => choices.find((c) => c.value === value)?.label ?? value

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="field"
          disabled={loading}
          aria-invalid={invalid || undefined}
          className="h-auto min-h-9 w-full flex-wrap gap-1 whitespace-normal px-2 py-1.5"
        >
          {loading ? (
            <span className="flex items-center gap-1.5 px-1 text-body-sm text-minimal">
              <CircleNotchIcon size={13} className="animate-spin" />
              Loading…
            </span>
          ) : selected.length === 0 ? (
            <span className="px-1 text-body-sm text-minimal">{placeholder}</span>
          ) : (
            selected.map((value) => (
              <span key={value} className="rounded bg-surface-muted px-1.5 py-0.5 text-[11px] text-default">
                {labelFor(value)}
              </span>
            ))
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="max-h-48 w-(--radix-popover-trigger-width) gap-0 overflow-y-auto border-default bg-surface-raised p-1"
      >
        {choices.map((choice) => {
          const isSelected = selected.includes(choice.value)
          return (
            <Button
              key={choice.value}
              type="button"
              variant="ghost"
              size="row"
              aria-pressed={isSelected}
              onClick={() => toggle(choice.value)}
              className="rounded-md text-xs"
            >
              <span
                className={cn(
                  `flex size-4 shrink-0 items-center justify-center rounded border`,
                  isSelected ? `border-strong bg-inverse text-inverse` : `border-strong`,
                )}
              >
                {isSelected && <CheckIcon size={10} weight="bold" />}
              </span>
              {choice.label}
            </Button>
          )
        })}
      </PopoverContent>
    </Popover>
  )
}

const truncate = (text: string, max = 18) => (text.length > max ? `${text.slice(0, max - 1)}…` : text)

/** On-demand variable inserter: a quiet "{} Insert variable" affordance that opens a popover with the
 *  grouped variable list (friendly labels + the value each resolves to for the preview record).
 *  Replaces the old always-visible chip wall — a dozen chips under every text field read as noise;
 *  a picker reads as a tool you reach for. Inserting closes the popover and returns the caret. */
function VariablePicker({
  variables,
  sample,
  onInsert,
}: {
  variables: ContactField[]
  sample?: Record<string, string>
  onInsert: (key: string) => void
}) {
  const [open, setOpen] = useState(false)
  const groups = groupFieldsBySource(variables)
  if (groups.length === 0) return null
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          `inline-flex items-center gap-1 rounded-md border border-default px-2 py-1 text-[10px] font-medium shadow-xs`,
          open ? `bg-surface-muted text-strong` : `bg-surface text-subtle hover:bg-surface-subtle hover:text-strong`,
        )}
      >
        <BracketsCurlyIcon size={11} weight="bold" /> Insert variable
      </button>
      {open && (
        <>
          {/* Invisible backdrop: click anywhere outside to close. */}
          <button
            type="button"
            aria-label="Close variables"
            tabIndex={-1}
            className="fixed inset-0 z-10 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div className="absolute left-0 z-20 mt-1 max-h-72 w-72 space-y-2 overflow-y-auto rounded-lg border border-default bg-surface-raised p-2.5 shadow-lg">
            {groups.map((group) => (
              <div key={group.source}>
                {groups.length > 1 && (
                  <div className="mb-1 text-[9px] font-semibold tracking-wide text-minimal uppercase">
                    {group.label}
                  </div>
                )}
                <div className="flex flex-wrap gap-1">
                  {group.fields.map((variable) => {
                    const resolved = sample?.[variable.key]
                    return (
                      <button
                        key={variable.key}
                        type="button"
                        title={resolved ? `${variable.label} → ${resolved}` : `Insert ${variable.label}`}
                        onClick={() => {
                          onInsert(variable.key)
                          setOpen(false)
                        }}
                        className="inline-flex items-center gap-1 rounded border border-default bg-surface-subtle px-1.5 py-0.5 text-[10px] font-medium text-subtle hover:bg-surface-muted"
                      >
                        {variable.label}
                        {resolved && <span className="text-minimal">{truncate(resolved)}</span>}
                      </button>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

/** Live "what it would say" preview of a token string, using the selected sample. Renders only when the
 *  sample actually has values to fill — otherwise there's nothing to preview. */
function RenderedPreview({
  value,
  variables,
  sample,
}: {
  value: string
  variables: ContactField[]
  sample?: Record<string, string>
}) {
  const hasSampleValues = !!sample && variables.some((v) => sample[v.key])
  if (!hasSampleValues || !value.trim()) return null
  return (
    <div className="mt-1.5 rounded-md border border-default bg-surface px-2 py-1.5">
      <div className="mb-0.5 text-[9px] font-semibold tracking-wide text-minimal uppercase">Preview</div>
      <p className="text-[11px] leading-relaxed whitespace-pre-wrap text-default">{fillVariables(value, sample!)}</p>
    </div>
  )
}

/** One row per token the text uses: "First name → [ there ]".
 *
 *  A token with no value sends nothing, which is how a patient once received "Hi , sorry we missed your
 *  call!" (TRK-5064). The fix is a fallback, and this is where it gets set. Deliberately the same shape
 *  as the bulk Send SMS modal, down to the wording: one habit, not two. Nobody types `??` anywhere; the
 *  syntax the engine reads is written into the message for them, so it stays portable.
 *
 *  Only tokens actually in the text appear, and the section vanishes when there are none, so an empty
 *  message shows no ceremony. */
function FallbackFields({
  value,
  onChange,
  variables,
}: {
  value: string
  onChange: (value: string) => void
  variables: ContactField[]
}) {
  const copy = useListCopy()
  const tokens = tokensUsed(value).filter((token) => token !== PAPERWORK_LINK_FIELD)
  if (tokens.length === 0) return null
  const current = readFallbacks(value)
  // Fall through to the global token labeler so a token OUTSIDE this trigger's variable scope
  // (e.g. contact.firstName on a call trigger without a matched contact) still reads as words.
  const labelFor = (token: string) =>
    variables.find((field) => engineVariableToken(field.key) === token)?.label ?? labelForToken(token, copy)
  return (
    <div className="mt-2 space-y-1.5">
      <Label className="text-[11px] text-subtle">
        If a contact is missing the value, send this instead
        <InfoHint text="Leave one blank and that part of the message is simply left out, which is how a text ends up reading “Hi ,”. Anything you type here is used only when the contact has no value." />
      </Label>
      {tokens.map((token) => (
        <div key={token} className="flex items-center gap-2">
          <span className="w-24 shrink-0 truncate text-[11px] text-subtle" title={token}>
            {labelFor(token)}
          </span>
          <Input
            value={current[token] ?? ``}
            placeholder="e.g. there"
            onChange={(e) => onChange(writeFallbacks(value, { ...current, [token]: e.target.value }))}
            className="h-7 flex-1 border-default bg-surface text-[11px]"
          />
        </div>
      ))}
    </div>
  )
}

/** Multiline message editor: textarea + variable inserters + live preview. Chips insert a `{{key}}` at
 *  the cursor, read as friendly names, and show their resolved value. `showSegments` adds the SMS
 *  char/segment counter (only meaningful for texts). */
export function MessageField({
  value,
  onChange,
  error,
  placeholder,
  variables,
  sample,
  showSegments = false,
  readOnly = false,
}: {
  value: string
  onChange: (value: string) => void
  error?: string
  placeholder?: string
  /** Locks the text and hides the variable picker. Used where the wording carries a commitment the
   *  practice is not free to change (see the caller-reply message in panels.tsx). */
  readOnly?: boolean
  variables: ContactField[]
  /** Field key → real value from the selected preview record; drives inline chip values + live preview. */
  sample?: Record<string, string>
  /** Accepted for caller compatibility; the chip editor auto-grows from a fixed minimum instead. */
  rows?: number
  showSegments?: boolean
}) {
  // Tokens render as chips ("First name"), never as `{{contact.firstName ?? "there"}}` syntax
  // (UX audit C3). The engine text stays the stored value; the chip carries it invisibly.
  const editorRef = useRef<TokenEditorHandle>(null)
  const segments = Math.max(1, Math.ceil(value.length / 160))
  return (
    <div>
      <TokenEditor
        ref={editorRef}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        readOnly={readOnly}
        invalid={!!error}
      />
      <div className="mt-1 flex items-center justify-between">
        {readOnly ? (
          <span />
        ) : (
          <VariablePicker
            variables={variables}
            sample={sample}
            onInsert={(key) => editorRef.current?.insertToken(engineVariableToken(key))}
          />
        )}
        {showSegments && (
          <div className="text-[11px] text-minimal">
            {value.length} chars · {segments} SMS
          </div>
        )}
      </div>
      <FallbackFields value={value} onChange={onChange} variables={variables} />
      <RenderedPreview value={value} variables={variables} sample={sample} />
    </div>
  )
}

/** Single-line variant of MessageField: an input with the same cursor-insert chips + live preview, for
 *  short fields (task title, a field value) that still want variable mapping. */
export function VariableTextField({
  value,
  onChange,
  error,
  placeholder,
  variables,
  sample,
}: {
  value: string
  onChange: (value: string) => void
  error?: string
  placeholder?: string
  variables: ContactField[]
  sample?: Record<string, string>
}) {
  // Same chip treatment as the SMS body (UX audit C3): tokens read as "First name", never as raw
  // {{...}} syntax, in single-line clothes.
  const editorRef = useRef<TokenEditorHandle>(null)
  return (
    <div>
      <TokenEditor
        ref={editorRef}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        invalid={!!error}
        multiline={false}
      />
      <div className="mt-1">
        <VariablePicker
          variables={variables}
          sample={sample}
          onInsert={(key) => editorRef.current?.insertToken(engineVariableToken(key))}
        />
      </div>
      <FallbackFields value={value} onChange={onChange} variables={variables} />
      <RenderedPreview value={value} variables={variables} sample={sample} />
    </div>
  )
}
